import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { BedDouble, CheckCircle2, Copy, FileDown, IndianRupee, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errText } from "@/lib/api";

const rupees = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const monthName = (m) =>
  new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });

export default function RentPay() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [claiming, setClaiming] = useState("");
  const [reference, setReference] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/rent/${token}`);
      setData(res.data);
    } catch (err) {
      setError(errText(err));
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = (value, label) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  const claim = async (month) => {
    setClaiming(month);
    try {
      const { data: res } = await api.post(`/rent/${token}/claim`, { month, reference });
      toast.success(
        res.status === "claimed"
          ? "Thanks — your PG owner has been notified to confirm it"
          : "You already told the owner about this month"
      );
      setReference("");
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setClaiming("");
    }
  };

  if (error)
    return (
      <div className="mx-auto max-w-lg px-5 py-24 text-center" data-testid="rent-pay-error">
        <p className="font-display text-2xl">Link no longer valid</p>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
      </div>
    );

  if (!data)
    return (
      <div className="mx-auto max-w-lg px-5 py-24">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );

  const pay = data.payment || {};
  const upiLink = pay.upi_id
    ? `upi://pay?pa=${encodeURIComponent(pay.upi_id)}&pn=${encodeURIComponent(data.stay_name || "PG")}` +
      `&am=${encodeURIComponent(data.total_due || 0)}&cu=INR&tn=${encodeURIComponent(
        `Rent ${data.bed_label || ""}`.trim()
      )}`
    : "";
  const bankRows = [
    ["UPI ID", pay.upi_id],
    ["Account name", pay.bank_account_name],
    ["Account number", pay.bank_account_number],
    ["IFSC", pay.bank_ifsc],
  ].filter(([, v]) => !!v);

  return (
    <div className="mx-auto max-w-2xl px-5 md:px-8 py-14" data-testid="rent-pay-page">
      <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-primary">Rent payment</p>
      <h1 className="mt-3 font-display text-3xl">Hi {data.tenant_name || "there"}</h1>
      <p className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
        <BedDouble className="h-4 w-4" /> {data.bed_label} · {data.stay_name}
        {data.stay_city ? `, ${data.stay_city}` : ""} · due on day {data.due_day}
      </p>

      {data.owed.length === 0 ? (
        <div className="mt-9 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-8" data-testid="rent-pay-settled">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" strokeWidth={1.8} />
          <p className="mt-4 font-display text-xl">Everything is settled</p>
          <p className="mt-2 text-sm text-muted-foreground">You have no pending rent right now. Thank you!</p>
        </div>
      ) : (
        <>
          <div className="mt-9 rounded-2xl border border-border p-6 md:p-8" data-testid="rent-pay-due-list">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <p className="font-display text-lg">
                {data.owed.length} month{data.owed.length > 1 ? "s" : ""} pending
              </p>
              <p className="flex items-center gap-1 font-display text-2xl">
                <IndianRupee className="h-5 w-5" />
                {Number(data.total_due).toLocaleString("en-IN")}
              </p>
            </div>
            <div className="mt-6 space-y-3">
              {data.owed.map((o) => {
                const claimed = (data.claimed_months || []).includes(o.month);
                return (
                  <div
                    key={o.month}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border px-4 py-3"
                    data-testid={`rent-due-row-${o.month}`}
                  >
                    <div>
                      <p className="text-sm font-bold">{monthName(o.month)}</p>
                      <p className="text-[12px] text-muted-foreground">{rupees(o.amount)}</p>
                    </div>
                    {claimed ? (
                      <span
                        className="flex items-center gap-1.5 text-[12px] font-bold text-amber-600"
                        data-testid={`rent-claimed-${o.month}`}
                      >
                        <Clock className="h-3.5 w-3.5" /> Waiting for owner to confirm
                      </span>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => claim(o.month)}
                        disabled={!!claiming}
                        className="h-11 rounded-lg font-bold"
                        data-testid={`rent-claim-btn-${o.month}`}
                      >
                        {claiming === o.month ? "Telling owner…" : "I've paid this month"}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-border p-6 md:p-8" data-testid="rent-pay-details">
            <p className="font-display text-lg">Pay your PG owner directly</p>
            {bankRows.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground" data-testid="rent-pay-no-details">
                Your PG owner has not added UPI or bank details yet — please contact them
                {data.contact_phone ? ` on ${data.contact_phone}` : ""}.
              </p>
            ) : (
              <>
                <p className="mt-2 text-[13px] text-muted-foreground">
                  Transfer {rupees(data.total_due)} using any of these, then tap "I've paid this month" above.
                </p>
                <div className="mt-5 space-y-2.5">
                  {bankRows.map(([label, value]) => (
                    <div
                      key={label}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-4 py-3"
                      data-testid={`rent-pay-detail-${label.toLowerCase().replace(/ /g, "-")}`}
                    >
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                          {label}
                        </p>
                        <p className="text-sm font-semibold">{value}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-lg"
                        onClick={() => copy(value, label)}
                        data-testid={`rent-copy-${label.toLowerCase().replace(/ /g, "-")}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                {upiLink && (
                  <div className="mt-6 flex flex-wrap items-center gap-6 rounded-xl border border-border p-5" data-testid="rent-upi-qr-block">
                    <div className="text-center">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/rent/${token}/qr`}
                        alt="Scan this UPI QR code to pay your rent"
                        width={168}
                        height={168}
                        className="rounded-lg"
                        data-testid="rent-upi-qr-image"
                      />
                      <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                        Scan to pay
                      </p>
                    </div>
                    <div className="min-w-[200px] flex-1">
                      <p className="text-sm font-bold">Scan with any UPI app</p>
                      <p className="mt-1.5 text-[13px] text-muted-foreground">
                        GPay, PhonePe, Paytm or your bank app — the amount {rupees(data.total_due)} and your PG's UPI ID
                        are already filled in, so there is nothing to type.
                      </p>
                      <a href={upiLink} data-testid="rent-upi-app-link">
                        <Button className="mt-4 h-12 rounded-lg font-bold hover:brightness-95">
                          Or open a UPI app to pay {rupees(data.total_due)}
                        </Button>
                      </a>
                    </div>
                  </div>
                )}
                {pay.note && <p className="mt-4 text-[13px] text-muted-foreground">{pay.note}</p>}
                <div className="mt-6 space-y-1.5">
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    UPI / UTR reference (optional)
                  </p>
                  <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="e.g. 4198XXXXXX21"
                    className="h-12 max-w-xs rounded-lg"
                    data-testid="rent-reference-input"
                  />
                  <p className="text-[12px] text-muted-foreground">
                    Adding it helps your owner match the payment faster.
                  </p>
                </div>
              </>
            )}
            {data.contact_phone && (
              <p className="mt-6 flex items-center gap-2 text-[13px] text-muted-foreground">
                <Phone className="h-4 w-4" /> Questions? Call {data.contact_phone}
              </p>
            )}
          </div>
        </>
      )}

      {data.history.length > 0 && (
        <div className="mt-8 rounded-2xl border border-border p-6" data-testid="rent-pay-history">
          <p className="font-display text-lg">Payment history</p>
          <div className="mt-4 space-y-2">
            {data.history.map((h) => (
              <div key={h.month} className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
                <span>{monthName(h.month)}</span>
                <span className="flex items-center gap-3 text-muted-foreground">
                  {rupees(h.amount)} · confirmed by owner
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/rent/${token}/receipt/${h.month}`}
                    className="inline-flex items-center gap-1 font-semibold text-primary underline-offset-4 hover:underline"
                    data-testid={`rent-receipt-link-${h.month}`}
                  >
                    <FileDown className="h-3.5 w-3.5" /> Receipt
                  </a>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
